import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  Upload,
  UploadFile,
  message,
} from "antd";
import {
  useCreateProductMutation,
  useGetAllMyProductCategoriesQuery,
  useGetAllMyProductOptionCategoriesQuery,
  useGetAllMyStoresQuery,
  useGetProductQuery,
  useUpdateProductMutation,
  useUploadFileMutation,
  useDeleteMediaMutation,
} from "../graphql/generated/hooks";
import { useForm } from "antd/es/form/Form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import RobinPage from "../components/core/RobinPage";
import { useEffect, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";

function ProductMutate() {
  const { t, i18n } = useTranslation();
  const [form] = useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);

  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const [createProduct, { loading: creating }] = useCreateProductMutation();
  const [updateProduct, { loading: updating }] = useUpdateProductMutation();
  const [uploadFile] = useUploadFileMutation();
  const [deleteMedia] = useDeleteMediaMutation();

  const productId = params.id ? Number(params.id) : null;
  const duplicateProductId = !productId
    ? Number((location.state as any)?.duplicateProductId ?? NaN)
    : NaN;
  const effectiveProductId = productId ?? (Number.isFinite(duplicateProductId) ? duplicateProductId : null);

  const tf = (key: string, fallback: string) =>
    t(key, { defaultValue: fallback }) as unknown as string;

  const { data: productData, loading: loadingProduct } = useGetProductQuery({
    variables: { id: effectiveProductId ?? 0 },
    skip: !effectiveProductId,
    // When navigating from `ProductList`, Apollo may already have `getProduct` data in cache
    // (via `getAllMyProducts`) and will not hit the network with the default `cache-first`.
    // We want the edit page to always re-fetch the latest product by id.
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const createPrefillProduct =
    !productId && Number.isFinite(duplicateProductId)
      ? productData?.getProduct
      : !productId
        ? (location.state as any)?.product
        : null;
  // Edit mode: always rely on the full `getProduct` query result (no location state).
  // Create/duplicate mode: can prefill from navigation state.
  const product = productId
    ? productData?.getProduct
    : createPrefillProduct ?? undefined;

  useEffect(() => {
    if (!productId) {
      setEditMode(false);
      if (Number.isFinite(duplicateProductId)) {
        if (loadingProduct) return;
        if (!createPrefillProduct) {
          messageApi.error(t("message.error.data_fetch_failed"));
          navigate(-1);
          return;
        }
      }
      if (createPrefillProduct?.image?.url && createPrefillProduct?.image?.id) {
        setFileList([
          {
            uid: "-1",
            name: "image.png",
            status: "done",
            url: createPrefillProduct.image.url,
            response: { id: createPrefillProduct.image.id },
          },
        ]);
      } else {
        setFileList([]);
      }
      setReady(true);
      return;
    }

    setEditMode(true);

    // When navigating directly to `/menu/products/mutate/:id`, `location.state`
    // may be missing; we fall back to the `getProduct` query.
    if (loadingProduct) return;

    // In edit mode, wait for the full `getProduct` payload before rendering the form.
    // The product coming from `location.state` (e.g. from list navigation) can be partial.
    const fullProduct = productData?.getProduct;
    if (!fullProduct) return;

    if (fullProduct.id !== productId) {
      messageApi.error(t("message.error.data_fetch_failed"));
      navigate(-1);
      return;
    }

    if (fullProduct.image?.url) {
      setFileList([
        {
          uid: "-1",
          name: "image.png",
          status: "done",
          url: fullProduct.image.url,
          response: { id: fullProduct.image.id },
        },
      ]);
    }

    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productId,
    duplicateProductId,
    loadingProduct,
    productData,
    createPrefillProduct,
    navigate,
    messageApi,
    t,
  ]);

  const { data: stores, loading: loadingStores } = useGetAllMyStoresQuery();
  const { data: categories, loading: loadingCategories } =
    useGetAllMyProductCategoriesQuery();
  const {
    data: productOptionCategories,
    loading: loadingProductOptionCategories,
  } = useGetAllMyProductOptionCategoriesQuery();
  const storesOptions = stores?.getAllStoresForPartner?.map((store) => ({
    label: store.name,
    value: store.id,
  }));
  const categoriesOptions = categories?.getAllProductCategoriesForPartner?.map(
    (category) => ({
      label: category.name,
      value: category.id,
    }),
  );
  // Stores are often already cached, while option categories may require a network call.
  // Also, the "all option categories" endpoint can return an empty array even when the
  // product already has selected option categories. To ensure selected tags always show,
  // merge query results with the product's selected categories.
  const productOptionCategoriesQueryOptions =
    productOptionCategories?.getAllProductOptionCategoriesForPartner?.map(
      (productOptionCategory) => ({
        label: productOptionCategory.name,
        value: productOptionCategory.id,
      }),
    ) ?? [];

  const productSelectedOptionCategoriesOptions = (product?.productOptionCategories ??
    []).map((poc: any) => ({
    label: poc.name,
    value: poc.id,
  }));

  const productOptionCategoriesOptions = [
    ...productOptionCategoriesQueryOptions,
    ...productSelectedOptionCategoriesOptions,
  ].filter(
    (opt, index, arr) => arr.findIndex((x) => x.value === opt.value) === index,
  );

  useEffect(() => {
    if (!ready) return;

    // Ensure the form field is actually populated once the product is ready.
    // This avoids relying on `initialValues` timing (which only applies on first mount).
    const current = form.getFieldValue("productOptionCategories");
    const fromProduct = (product?.productOptionCategories ?? [])
      .map((x: any) => Number(x?.id))
      .filter((x: any) => Number.isFinite(x));

    if (
      (current === undefined || (Array.isArray(current) && current.length === 0)) &&
      fromProduct.length > 0
    ) {
      form.setFieldValue("productOptionCategories", fromProduct);
    }
  }, [
    ready,
    product,
    form,
  ]);

  const requiredMultiSelectValidator =
    (msg: string) => async (_: any, value: any) => {
      if (!Array.isArray(value) || value.length === 0) throw new Error(msg);
    };

  const trimToString = (v: unknown) => {
    if (typeof v !== "string") return "";
    return v.trim();
  };

  const onFinish = async (values: any) => {
    const name = trimToString(values.name);
    const nameAr = trimToString(values.nameAr);
    const description = trimToString(values.description);
    const descriptionAr = trimToString(values.descriptionAr);

    const price =
      typeof values.price === "number"
        ? values.price
        : Number.parseFloat(String(values.price ?? ""));

    if (!name) {
      messageApi.error(t("message.error.name_required"));
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      messageApi.error(t("message.error.price_invalid"));
      return;
    }
    if (!values.category) {
      messageApi.error(t("message.error.category_required"));
      return;
    }
    if (!Array.isArray(values.stores) || values.stores.length === 0) {
      messageApi.error(t("message.error.stores_required"));
      return;
    }

    if (editMode)
      await updateProduct({
        variables: {
          id: product?.id,
          name,
          nameAr,
          description,
          descriptionAr,
          price,
          productCategoryId: values.category,
          storeIds: values.stores,
          productOptionCategoryIds: values.productOptionCategories,
          imageId: fileList[0]?.response?.id,
          foodQualityTimeMinutes: values.foodQualityTimeMinutes,
        },
      })
        .then(() => {
          messageApi.success(t("message.success.data_updated"));
          navigate(-1);
        })
        .catch((error) => {
          messageApi.error(t("message.error.data_update_failed"));
        });
    else
      await createProduct({
        variables: {
          name,
          nameAr,
          description,
          descriptionAr,
          price,
          productCategoryId: values.category,
          storeIds: values.stores,
          productOptionCategoryIds: values.productOptionCategories,
          imageId: fileList[0]?.response?.id,
          foodQualityTimeMinutes: values.foodQualityTimeMinutes,
        },
      })
        .then(() => {
          messageApi.success(t("message.success.data_created"));
          navigate(-1);
        })
        .catch((error) => {
          messageApi.error(t("message.error.data_create_failed"));
        });
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleCustomUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;

    try {
      onProgress({ percent: 0 });

      // Convert file to base64
      const base64String = await convertFileToBase64(file);
      onProgress({ percent: 50 });

      // Upload using GraphQL mutation
      const result = await uploadFile({
        variables: {
          base64File: base64String,
          originalName: file.name,
          folder: "products",
        },
      });

      onProgress({ percent: 100 });

      if (result.data?.uploadFile) {
        onSuccess(
          {
            id: result.data.uploadFile.id,
            url: result.data.uploadFile.url,
          },
          file,
        );
      } else {
        onError(new Error("Upload failed: No data returned"));
      }
    } catch (error) {
      onError(error);
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  return (
    <RobinPage
      title={editMode ? t("title.edit_product") : t("title.add_product")}
    >
      <Spin
        spinning={!ready}
        style={{ width: "100%" }}
      >
        <div style={{ minHeight: 520 }}>
          <Row>
            {contextHolder}
            <Col span={24}>
              {ready ? (
                <Card
                  bordered={false}
                  style={{ width: "100%", boxShadow: "0 6px 22px rgba(0, 0, 0, 0.06)" }}
                  styles={{ body: { padding: 24 } }}
                >
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    dir={i18n.dir()}
                    name="mutateProduct"
                    requiredMark="optional"
                    initialValues={
                      editMode
                        ? {
                            ...product,
                            foodQualityTimeMinutes:
                              product?.foodQualityTimeMinutes ?? 20,
                            category: product?.productCategory?.id,
                            stores: (product?.stores ?? []).map(
                              (store: any) => store.id,
                            ),
                            productOptionCategories:
                              (product?.productOptionCategories ?? []).map(
                                (productOptionCategory: any) =>
                                  productOptionCategory.id,
                              ),
                          }
                        : createPrefillProduct
                          ? {
                              ...createPrefillProduct,
                              foodQualityTimeMinutes:
                                createPrefillProduct?.foodQualityTimeMinutes ?? 20,
                              category: createPrefillProduct?.productCategory?.id,
                              stores: (createPrefillProduct?.stores ?? []).map(
                                (store: any) => store.id,
                              ),
                              productOptionCategories:
                                (createPrefillProduct?.productOptionCategories ?? []).map(
                                  (productOptionCategory: any) =>
                                    productOptionCategory.id,
                                ),
                            }
                          : {
                              foodQualityTimeMinutes: 20,
                            }
                    }
                  >
                    <Typography.Title level={5} style={{ marginTop: 0 }}>
                      {tf("label.image", "Image")}
                    </Typography.Title>
              <Form.Item name="file" label="Upload">
                <Upload
                  maxCount={1}
                  name="file"
                  listType="picture-card"
                  onRemove={async (file) => {
                    try {
                      // If the file has been uploaded and has an ID, delete it from the server
                      if (file.response?.id) {
                        await deleteMedia({
                          variables: {
                            id: file.response.id,
                          },
                        });
                        message.success(`${file.name} deleted successfully`);
                      }
                    } catch (error) {
                      message.error(`Failed to delete ${file.name}`);
                    } finally {
                      // Always clear the form and file list regardless of delete success/failure
                      form.setFieldValue("imageId", null);
                      setFileList([]);
                    }
                  }}
                  customRequest={handleCustomUpload}
                  onChange={({ fileList: newFileList }) => {
                    if (newFileList[0]?.status === "done") {
                      message.success(
                        `${newFileList[0].name} file uploaded successfully`,
                      );
                      // Set the uploaded image ID in the form
                      form.setFieldValue(
                        "imageId",
                        newFileList[0].response?.id,
                      );
                    } else if (newFileList[0]?.status === "error") {
                      message.error(
                        `${newFileList[0].name} file upload failed.`,
                      );
                    }
                    setFileList(newFileList);
                  }}
                  fileList={fileList}
                >
                  {fileList.length ? null : uploadButton}
                </Upload>
              </Form.Item>

                    <Divider style={{ margin: "16px 0" }} />
                    <Typography.Title level={5} style={{ marginTop: 0 }}>
                      {tf("label.basic_info", "Basic info")}
                    </Typography.Title>

                    <Row gutter={12}>
                      <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  { required: true, whitespace: true, message: t("message.error.name_required") },
                  { max: 120, message: t("message.error.max_length_120") },
                ]}
              >
                        <Input placeholder={t("placeholder.name") || ""} />
              </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
              <Form.Item
                name="nameAr"
                label="Name Ar"
                rules={[{ max: 120, message: t("message.error.max_length_120") }]}
              >
                        <Input placeholder={t("placeholder.name_ar") || ""} />
              </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={12}>
                      <Col xs={24} md={12}>
              <Form.Item
                name="description"
                label="Description"
                rules={[{ max: 500, message: t("message.error.max_length_500") }]}
              >
                        <Input placeholder={t("placeholder.description") || ""} />
              </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
              <Form.Item
                name="descriptionAr"
                label="Description Ar"
                rules={[{ max: 500, message: t("message.error.max_length_500") }]}
              >
                        <Input placeholder={t("placeholder.description_ar") || ""} />
              </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={12}>
                      <Col xs={24} md={12}>
              <Form.Item
                name="price"
                label="Price"
                rules={[
                  {
                    required: true,
                    message: t("message.error.price_required"),
                  },
                  {
                    type: "number",
                    min: 0,
                    message: t("message.error.price_invalid"),
                  },
                ]}
              >
                        <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[
                  {
                    required: true,
                    message: t("message.error.category_required"),
                  },
                ]}
              >
                        <Select
                          loading={loadingCategories}
                          showSearch
                          optionFilterProp="label"
                          placeholder={tf("placeholder.select", "Select")}
                          options={categoriesOptions}
                        />
              </Form.Item>
                      </Col>
                    </Row>

                    <Divider style={{ margin: "16px 0" }} />
                    <Typography.Title level={5} style={{ marginTop: 0 }}>
                      {tf("label.availability", "Availability")}
                    </Typography.Title>

                    <Row gutter={12}>
                      <Col xs={24} md={12}>
              <Form.Item
                name="stores"
                label="Stores"
                rules={[
                  { validator: requiredMultiSelectValidator(t("message.error.stores_required")) },
                ]}
              >
                        <Select
                          mode="multiple"
                          loading={loadingStores}
                          options={storesOptions}
                          showSearch
                          optionFilterProp="label"
                          placeholder={tf("placeholder.select", "Select")}
                        />
              </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
              <Form.Item
                name="productOptionCategories"
                label="Option Categories"
              >
                        <Select
                          mode="multiple"
                          loading={loadingProductOptionCategories}
                          options={productOptionCategoriesOptions}
                          showSearch
                          optionFilterProp="label"
                          placeholder={tf("placeholder.select", "Select")}
                        />
              </Form.Item>
                      </Col>
                    </Row>

                    <Divider style={{ margin: "16px 0" }} />
                    <Typography.Title level={5} style={{ marginTop: 0 }}>
                      {tf("label.preparation", "Preparation")}
                    </Typography.Title>

                    <Row gutter={12}>
                      <Col xs={24} md={12}>
              <Form.Item
                name="foodQualityTimeMinutes"
                label={t("label.food_quality_time_minutes")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "message.error.food_quality_time_minutes_required",
                    ),
                  },
                ]}
              >
                        <InputNumber
                          min={1}
                          max={1440}
                          precision={0}
                          style={{ width: "100%" }}
                          addonAfter={t("addonAfter.minutes")}
                        />
              </Form.Item>
                      </Col>
                    </Row>
              <Form.Item>
                      <Row justify="end">
                        <Space>
                          <Button
                            onClick={() => {
                              navigate(-1);
                            }}
                          >
                            {t("button.cancel")}
                          </Button>
                          <Button
                            loading={creating || updating}
                            type="primary"
                            htmlType="submit"
                          >
                            {t("button.save")}
                          </Button>
                        </Space>
                      </Row>
              </Form.Item>
                  </Form>
                </Card>
              ) : null}
            </Col>
          </Row>
        </div>
      </Spin>
    </RobinPage>
  );
}

export default ProductMutate;
